import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Users, Calendar, CheckSquare, MapPin, Package, Search, LogOut, Building2, Shield, UserCog, Layers, Image, ImagePlus, Key, Mail, Menu, X, Activity, KeyRound, CalendarDays, Contact, ListChecks } from 'lucide-react';
import { cn } from '@/lib/utils';
import { isSocialCardsEnabled } from '@/config/features';
import { GlobalSearch } from './GlobalSearch';
import { ThemeToggle } from './ThemeToggle';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { apiRequest } from '@/lib/api';
import { validatePasswordStrength } from '@littlebooker/dal';

const navigation = [
  { name: 'Programs', href: '/programs', icon: CalendarDays },
  { name: 'Happenings', href: '/happenings', icon: Calendar },
  { name: 'People', href: '/people', icon: Users },
  { name: 'Tasks', href: '/tasks', icon: CheckSquare },
  { name: 'Places', href: '/places', icon: MapPin },
  { name: 'Things', href: '/things', icon: Package },
  { name: 'Selections', href: '/selections', icon: ListChecks },
  { name: 'Social Cards', href: '/social-cards', icon: ImagePlus },
];

const settingsNavigation = [
  { name: 'Organization', href: '/account', icon: Building2 },
  { name: 'API Keys', href: '/api-keys', icon: Key },
  { name: 'Staff Roles', href: '/staff-roles', icon: Contact },
  { name: 'Templates', href: '/templates', icon: Layers },
  { name: 'Email Templates', href: '/email-templates', icon: Mail, ownerOnly: true },
  { name: 'Auth Events', href: '/auth-events', icon: Activity, ownerOnly: true },
  { name: 'Users', href: '/users', icon: UserCog },
  { name: 'Media Manager', href: '/media', icon: Image },
];

function ChangePasswordDialog({ open, onOpenChange, onSuccess }: { open: boolean; onOpenChange: (open: boolean) => void; onSuccess: () => void }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const strengthError = validatePasswordStrength(newPassword);
    if (strengthError) {
      setError(strengthError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await apiRequest('/api/v1/users/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      setSuccess(true);
      setTimeout(() => {
        onOpenChange(false);
        reset();
        onSuccess();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change Password</DialogTitle>
          <DialogDescription>Enter your current password and choose a new one.</DialogDescription>
        </DialogHeader>
        {success ? (
          <p className="text-sm text-emerald-600 px-4 sm:px-6 py-4">Password changed successfully! Logging you out...</p>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 px-4 sm:px-6 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Current password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                required
                autoComplete="current-password"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">New password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Confirm new password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            </div>
            <DialogFooter>
              <button
                type="button"
                onClick={() => { onOpenChange(false); reset(); }}
                className="rounded-md border border-input px-4 py-2 text-sm hover:bg-accent"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {loading ? 'Changing...' : 'Change password'}
              </button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AccountInfo() {
  const { account, user, logout } = useAuth();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  if (!account) return null;

  return (
    <div className="space-y-2 px-3 py-2">
      {user && (
        <div className="space-y-1">
          <div className="flex items-start gap-2">
            <UserCog className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="font-medium block text-sm">{user.name}</span>
              <span className="text-xs text-muted-foreground block">{user.email}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 pl-6">
            <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded uppercase font-semibold ${user.role === 'owner' ? 'bg-purple-badge text-purple-badge-foreground' : 'bg-blue-badge text-blue-badge-foreground'
              }`}>
              {user.role}
            </span>
            <button
              onClick={() => setChangePasswordOpen(true)}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              title="Change your password"
            >
              <KeyRound className="h-3 w-3" />
              <span>Change password</span>
            </button>
          </div>
        </div>
      )}
      <div className="flex items-center gap-2 text-sm">
        <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <span className="font-medium">{account.name}</span>
        {account.type === 'admin' && (
          <span title="Admin Account"><Shield className="h-3 w-3 text-warning flex-shrink-0" /></span>
        )}
        {account.type === 'dev' && (
          <span className="text-xs bg-blue-badge text-blue-badge-foreground px-1.5 py-0.5 rounded flex-shrink-0">DEV</span>
        )}
      </div>
      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} onSuccess={logout} />
    </div>
  );
}

function LogoutButton() {
  const { logout, account } = useAuth();

  if (!account) return null;

  return (
    <button
      onClick={logout}
      className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
    >
      <LogOut className="h-4 w-4" />
      <span>Sign out</span>
    </button>
  );
}

export default function Layout() {
  const location = useLocation();
  const { user, account } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Update document title based on account name
  useEffect(() => {
    if (account?.name) {
      document.title = `LittleBooker - ${account.name}`;
    }
  }, [account?.name]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Filter settings navigation based on user role
  const filteredSettingsNavigation = settingsNavigation.filter((item) => {
    // Only show owner-only items to owner users
    if (item.ownerOnly) {
      return user?.role === 'owner';
    }
    return true;
  });

  // Social Cards is Radio-Weesper-specific; hide it for other accounts.
  const filteredNavigation = navigation.filter(
    (item) => item.href !== '/social-cards' || isSocialCardsEnabled(account?.slug),
  );

  const openGlobalSearch = () => {
    const event = new KeyboardEvent('keydown', {
      key: 'k',
      metaKey: true,
      bubbles: true,
    });
    document.dispatchEvent(event);
  };

  const sidebarContent = (
    <>
      <nav className="p-4 flex-1 overflow-y-auto">
        <ul className="space-y-1">
          {filteredNavigation.map((item) => {
            const Icon = item.icon;
            // Happenings is the default view, so also highlight it when on home page
            const isActive = location.pathname === item.href ||
              (item.href === '/happenings' && location.pathname === '/');
            return (
              <li key={item.name}>
                <Link
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground dark:bg-transparent dark:border dark:border-primary dark:text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                  )}
                >
                  <Icon className="h-5 w-5" />
                  {item.name}
                </Link>
              </li>
            );
          })}
        </ul>
        <div className="mt-4 border-t pt-4">
          <button
            type="button"
            onClick={openGlobalSearch}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <Search className="h-5 w-5" />
            <span className="flex-1 text-left">Search</span>
            <kbd className="pointer-events-none hidden sm:inline-block rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px] font-medium">
              ⌘K
            </kbd>
          </button>
        </div>
        <div className="mt-4 border-t pt-4">
          <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Settings</p>
          <ul className="space-y-1">
            {filteredSettingsNavigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-primary text-primary-foreground dark:bg-transparent dark:border dark:border-primary dark:text-primary'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    {item.name}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </nav>

      {/* Account info and logout at bottom */}
      <div className="border-t p-4 space-y-1">
        <ThemeToggle />
        <AccountInfo />
        <LogoutButton />
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      <GlobalSearch />

      {/* Mobile header - visible on small screens */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-40 h-14 border-b bg-card flex items-center justify-between px-4">
        <button
          type="button"
          onClick={() => setMobileMenuOpen(true)}
          className="p-2 -ml-2 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Open menu"
        >
          <Menu className="h-6 w-6" />
        </button>
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="LittleBooker" className="h-7 w-7 rounded" />
          <span className="font-semibold">LittleBooker</span>
        </div>
        <button
          type="button"
          onClick={openGlobalSearch}
          className="p-2 -mr-2 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Search"
        >
          <Search className="h-5 w-5" />
        </button>
      </header>

      {/* Mobile menu backdrop */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-background/80 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile menu drawer */}
      <aside
        className={cn(
          'md:hidden fixed inset-y-0 left-0 z-50 w-72 bg-card border-r flex flex-col transform transition-transform duration-300 ease-in-out',
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-14 items-center justify-between border-b px-4">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="LittleBooker" className="h-7 w-7 rounded" />
            <span className="font-semibold">LittleBooker</span>
          </div>
          <button
            type="button"
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 -mr-2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {sidebarContent}
      </aside>

      <div className="flex min-h-screen">
        {/* Desktop sidebar - hidden on mobile */}
        <aside className="hidden md:flex w-64 border-r bg-card flex-col sticky top-0 h-screen">
          <div className="flex h-16 items-center gap-3 border-b px-6">
            <img src="/logo.png" alt="LittleBooker" className="h-8 w-8 rounded" />
            <h1 className="text-xl font-bold">LittleBooker</h1>
          </div>
          {sidebarContent}
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto pt-14 md:pt-0">
          <div className="container mx-auto p-4 md:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
